import { useState } from "react";
import { api, type User, type UserInput } from "../api";
import BackHeader from "../components/BackHeader";
import PinDialog from "../components/PinDialog";
import { formatPhone } from "../components/UserPicker";
import { SmsHistory } from "../screens/SmsScreen";

const FIELD = "w-full rounded-[14px] bg-white px-4 font-semibold outline-none placeholder:text-ink/40 border border-line focus:border-[#e39aa8] focus:ring-[3px] focus:ring-rose/20";
const LABEL = "px-1 text-[12.5px] font-semibold text-[#3d2930]";
const COUNTRY_CODE = "+47";

const localDigits = (phone: string | null | undefined) =>
  phone?.startsWith(COUNTRY_CODE) ? phone.slice(COUNTRY_CODE.length) : (phone ?? "").replace(/\D/g, "");

function UserForm({
  initial,
  pin,
  onSaved,
  onCancel,
}: {
  initial: User | null;
  pin: string;
  onSaved: (user: User) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [phone, setPhone] = useState(localDigits(initial?.phone));
  const [isDriver, setIsDriver] = useState(initial?.isDriver ?? true);
  const [isAdmin, setIsAdmin] = useState(initial?.isAdmin ?? false);
  const [isOrderDriver, setIsOrderDriver] = useState(initial?.isOrderDriver ?? false);
  const [active, setActive] = useState(initial?.active ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    const input: UserInput = {
      name: name.trim(),
      description: description.trim(),
      phone: phone ? `${COUNTRY_CODE}${phone}` : "",
      isDriver,
      isAdmin,
      isOrderDriver,
      active,
    };
    try {
      onSaved(initial ? await api.updateUser(initial.id, input, pin) : await api.createUser(input, pin));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save user");
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-1 flex-col gap-5">
      <BackHeader title={initial ? "Edit user" : "New user"} onBack={onCancel} />
      <form
        className="pop-in flex flex-1 flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <label className="flex flex-col gap-1.5">
          <span className={LABEL}>
            Name <span className="text-hot">*</span>
          </span>
          <input autoFocus={!initial} type="text" required maxLength={60} value={name} onChange={(e) => setName(e.target.value)} className={`${FIELD} h-14`} />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className={LABEL}>Description</span>
          <input type="text" maxLength={200} placeholder="Kitchen, weekend driver, office…" value={description} onChange={(e) => setDescription(e.target.value)} className={`${FIELD} h-14`} />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className={LABEL}>Telephone</span>
          <div className="flex h-14 items-center overflow-hidden rounded-[14px] bg-white border border-line-soft shadow-card border-line focus-within:border-[#e39aa8] focus-within:ring-[3px] focus-within:ring-rose/20">
            <span className="flex h-full items-center bg-blush-soft px-4 font-bold text-ink/60">{COUNTRY_CODE}</span>
            <input
              type="tel"
              inputMode="numeric"
              placeholder="41261013"
              value={phone}
              maxLength={12}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
              className="h-full min-w-0 flex-1 bg-transparent px-4 font-semibold outline-none placeholder:text-ink/40"
            />
          </div>
        </label>
        <label className="flex h-14 cursor-pointer select-none items-center gap-3 rounded-[14px] bg-white px-4 font-semibold border border-line-soft shadow-card">
          <input type="checkbox" checked={isDriver} onChange={(e) => setIsDriver(e.target.checked)} className="size-5 accent-[#d8647a]" />
          Is driver
          <span className="ml-auto text-xs text-ink/50">shown in box tracker and filter</span>
        </label>
        <label className="flex h-14 cursor-pointer select-none items-center gap-3 rounded-[14px] bg-white px-4 font-semibold border border-line-soft shadow-card">
          <input type="checkbox" checked={isAdmin} onChange={(e) => setIsAdmin(e.target.checked)} className="size-5 accent-[#d8647a]" />
          Is admin
          <span className="ml-auto text-xs text-ink/50">hidden from the view-only PIN</span>
        </label>
        <label className="flex h-14 cursor-pointer select-none items-center gap-3 rounded-[14px] bg-white px-4 font-semibold border border-line-soft shadow-card">
          <input type="checkbox" checked={isOrderDriver} onChange={(e) => setIsOrderDriver(e.target.checked)} className="size-5 accent-[#d8647a]" />
          Is order driver
          <span className="ml-auto text-xs text-ink/50">offered in the order app</span>
        </label>
        {initial && (
          <label className="flex h-14 cursor-pointer select-none items-center gap-3 rounded-[14px] bg-white px-4 font-semibold border border-line-soft shadow-card">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="size-5 accent-[#d8647a]" />
            Active
            <span className="ml-auto text-xs text-ink/50">untick to disable</span>
          </label>
        )}
        {error && <p className="text-center text-sm font-semibold text-danger">{error}</p>}
        <div className="mt-auto flex gap-2 pt-2">
          <button type="button" onClick={onCancel} className="h-14 flex-1 rounded-[14px] bg-white font-bold border border-line">
            Cancel
          </button>
          <button type="submit" disabled={!name.trim() || busy} className="h-14 flex-1 rounded-[14px] bg-rose-grad font-bold text-white disabled:opacity-50 shadow-button">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

/** Users page (person icon in the Scheduler header): list, add, edit, disable. PIN asked once. */
export default function UsersScreen({
  users,
  onChanged,
  onBack,
}: {
  users: User[];
  onChanged: (user: User) => void;
  onBack: () => void;
}) {
  const [pin, setPin] = useState<string | null>(null);
  const [pending, setPending] = useState<{ user: User | null } | null>(null); // what to edit after PIN
  const [editing, setEditing] = useState<{ user: User | null } | null>(null);
  const [showSms, setShowSms] = useState(false);

  if (showSms) return <SmsHistory embedded onBack={() => setShowSms(false)} />;

  const startEdit = (user: User | null) => {
    if (pin) setEditing({ user });
    else setPending({ user });
  };

  if (editing && pin) {
    return (
      <UserForm
        initial={editing.user}
        pin={pin}
        onSaved={(u) => {
          onChanged(u);
          setEditing(null);
        }}
        onCancel={() => setEditing(null)}
      />
    );
  }

  const active = users.filter((u) => u.active);
  const disabled = users.filter((u) => !u.active);
  const Row = ({ u }: { u: User }) => (
    <li>
      <button onClick={() => startEdit(u)} className={`flex w-full items-center gap-3 rounded-[14px] bg-white px-4 py-3 text-left border border-line-soft shadow-card ${u.active ? "" : "opacity-50"}`}>
        <div className="min-w-0 flex-1">
          <div className="font-semibold">{u.name}</div>
          <div className="truncate text-xs font-semibold text-ink/50">
            {[u.description, formatPhone(u.phone)].filter(Boolean).join(" · ") || "No details"}
          </div>
        </div>
        {u.isAdmin && <span className="shrink-0 rounded-full bg-lemon px-3 py-1 text-xs font-bold">Admin</span>}
        {u.isDriver && <span className="shrink-0 rounded-full bg-lavender px-3 py-1 text-xs font-bold">Driver</span>}
        {u.isOrderDriver && <span className="shrink-0 rounded-full bg-lavender px-3 py-1 text-xs font-bold">Order driver</span>}
      </button>
    </li>
  );

  return (
    <div className="flex flex-1 flex-col gap-5">
      <BackHeader
        title="Users"
        onBack={onBack}
        action={
          <div className="flex shrink-0 items-center gap-2">
            <button onClick={() => setShowSms(true)} className="flex h-10 items-center rounded-[12px] border border-[#efdce0] bg-white px-3.5 text-sm font-bold text-[#3a1c25] active:scale-95">
              SMS log
            </button>
            <button onClick={() => startEdit(null)} className="flex h-10 items-center rounded-[12px] bg-rose-grad px-3.5 text-sm font-bold text-white shadow-button active:scale-95">
              + Add
            </button>
          </div>
        }
      />
      {users.length === 0 && <p className="py-8 text-center font-semibold text-ink/50">No users yet.</p>}
      <ul className="flex flex-col gap-2">
        {active.map((u) => (
          <Row key={u.id} u={u} />
        ))}
      </ul>
      {disabled.length > 0 && (
        <section className="flex flex-col gap-2">
          <h2 className={LABEL}>Disabled</h2>
          <ul className="flex flex-col gap-2">
            {disabled.map((u) => (
              <Row key={u.id} u={u} />
            ))}
          </ul>
        </section>
      )}
      <p className="text-center text-xs font-semibold text-ink/40">Tap a user to edit. Changes need the admin PIN once.</p>

      {pending && (
        <PinDialog
          title="Edit users"
          detail="Enter the PIN to add or change users."
          confirmLabel="Continue"
          confirmBusyLabel="Checking…"
          confirmClass="bg-rose-grad text-white shadow-button"
          onConfirm={async (entered) => {
            await api.verifyPin(entered);
            setPin(entered);
            setEditing(pending);
            setPending(null);
          }}
          onCancel={() => setPending(null)}
        />
      )}
    </div>
  );
}
